<script lang="ts">
	import { Alert, Skeleton, SkeletonGroup } from '@navitech/ui';
	import { route } from './lib/router';
	import { loadIdentity } from './lib/identity.svelte';
	import { loadFeatures } from './lib/features.svelte';
	import Layout from './components/Layout.svelte';

	// On entry (past the login screen): resolve the operator's own IP (for the
	// "this is your IP" badges) and the runtime feature flags (so ban copy
	// reflects whether enforcement is actually on). Both no-op once loaded.
	$effect(() => {
		if ($route.name !== 'login') {
			void loadIdentity();
			void loadFeatures();
		}
	});

	// Login and Dashboard are the two first-paint screens, so they ride in the
	// main chunk. Every other route is a dynamic import: Vite emits one chunk
	// per page and the browser downloads it the first time you navigate there.
	// Before this, all 20 routes — every drill-down, every settings form — were
	// in the initial download of the login screen.
	import Login from './routes/Login.svelte';
	import Dashboard from './routes/Dashboard.svelte';

	type PageModule = { default: import('svelte').Component<Record<string, never>> };

	const LAZY: Record<string, () => Promise<PageModule>> = {
		traffic: () => import('./routes/Traffic.svelte'),
		events: () => import('./routes/Events.svelte'),
		eventDetail: () => import('./routes/EventDetail.svelte'),
		httpRequests: () => import('./routes/HttpRequests.svelte'),
		detections: () => import('./routes/Detections.svelte'),
		detectionDetail: () => import('./routes/DetectionDetail.svelte'),
		banReview: () => import('./routes/BanReview.svelte'),
		bans: () => import('./routes/Bans.svelte'),
		services: () => import('./routes/Services.svelte'),
		sources: () => import('./routes/Sources.svelte'),
		trustedIps: () => import('./routes/TrustedIPs.svelte'),
		settings: () => import('./routes/Settings.svelte'),
		audit: () => import('./routes/Audit.svelte'),
		clientDetail: () => import('./routes/ClientDetail.svelte'),
		originDetail: () => import('./routes/OriginDetail.svelte'),
		serviceInsights: () => import('./routes/ServiceInsights.svelte'),
		portDetail: () => import('./routes/PortDetail.svelte'),
		domains: () => import('./routes/Domains.svelte'),
		domainDetail: () => import('./routes/DomainDetail.svelte'),
		edge: () => import('./routes/Edge.svelte'),
		edgeApp: () => import('./routes/EdgeApp.svelte'),
	};

	// A chunk that has already been fetched resolves from the module cache on
	// the next visit, so re-navigating never re-downloads or flashes.
	const pending = $derived(LAZY[$route.name]?.());
</script>

{#if $route.name === 'login'}
	<Login />
{:else}
	<Layout>
		{#if $route.name === 'dashboard'}
			<Dashboard />
		{:else if pending}
			{#await pending}
				<!-- Chunk in flight. Holds the page's shape for the few ms it
				     takes on a warm connection. -->
				<SkeletonGroup label="Loading page">
					<Skeleton class="mb-4" width={224} height={32} />
					<div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
						{#each [1, 2, 3, 4] as i (i)}
							<Skeleton height={96} />
						{/each}
					</div>
				</SkeletonGroup>
			{:then mod}
				{@const Page = mod.default}
				<Page />
			{:catch}
				<Alert tone="err">
					This page failed to load. That usually means a new version was deployed while the tab
					was open — reload to pick it up.
				</Alert>
			{/await}
		{/if}
	</Layout>
{/if}
